// Response coverage validation utilities

export const getQuestionResponseDomain = (questionNode) => {
  const { type, choices, minValue, maxValue } = questionNode.data;
  
  switch (type) {
    case 'yes_no':
      return {
        type: 'discrete',
        values: ['yes', 'no'],
        labels: ['Yes', 'No']
      };
      
    case 'choice':
      return {
        type: 'discrete', 
        values: choices || [],
        labels: choices || []
      };
      
    case 'scale':
      const min = minValue || 1;
      const max = maxValue || 10;
      return {
        type: 'numeric',
        min,
        max,
        suggestion: `Consider ranges like 1–${Math.floor((min + max) / 2)} and ${Math.floor((min + max) / 2) + 1}–${max} to split the scale.`
      };
      
    case 'number':
      return {
        type: 'numeric',
        min: minValue,
        max: maxValue,
        suggestion: 'Consider defining ranges or thresholds to route different responses.'
      };
      
    case 'text':
    case 'email':
      return {
        type: 'text',
        suggestion: 'Consider conditions like contains "keyword", equals "value", or length > 10.'
      };
      
    default:
      return {
        type: 'unknown',
        suggestion: 'Consider defining conditions for different response paths.'
      };
  }
};

export const validateResponseCoverage = (questionNode, outgoingEdges) => {
  const domain = getQuestionResponseDomain(questionNode);
  const issues = [];
  
  if (domain.type === 'discrete') {
    // Check coverage for yes/no or multiple choice
    const coveredValues = new Set();
    let hasDefaultPath = false;
    
    outgoingEdges.forEach(edge => {
      const condition = edge.data?.condition?.toLowerCase() || '';
      const label = edge.data?.label?.toLowerCase() || '';
      
      if (!condition && !label) {
        hasDefaultPath = true;
        return;
      }
      
      // Check which values this edge covers
      domain.values.forEach(value => {
        const valueLower = value.toLowerCase();
        if (condition.includes(`"${valueLower}"`) || 
            condition.includes(`'${valueLower}'`) || 
            condition.includes(`== ${valueLower}`) ||
            label.includes(valueLower)) {
          coveredValues.add(value);
        }
      });
    });
    
    // Find missing values
    const uncoveredValues = domain.values.filter(value => !coveredValues.has(value));
    
    if (uncoveredValues.length > 0 && !hasDefaultPath) {
      uncoveredValues.forEach(value => {
        issues.push({
          type: 'missing_path',
          message: `No coverage yet for the "${value}" response.`,
          severity: 'warning'
        });
      });
    }
  } else if (domain.type === 'numeric') {
    // For numeric, just check if there are any conditions
    const hasConditions = outgoingEdges.some(edge => edge.data?.condition);
    const hasDefaultPath = outgoingEdges.some(edge => !edge.data?.condition);
    
    if (!hasConditions && !hasDefaultPath) {
      issues.push({
        type: 'no_conditions',
        message: 'No numeric conditions yet for different response values.',
        severity: 'info',
        suggestion: domain.suggestion
      });
    }
  } else if (domain.type === 'text') {
    // For text, check if there are meaningful conditions
    const hasConditions = outgoingEdges.some(edge => edge.data?.condition);
    const hasDefaultPath = outgoingEdges.some(edge => !edge.data?.condition);
    
    if (!hasConditions && !hasDefaultPath) {
      issues.push({
        type: 'no_conditions',
        message: 'No text conditions yet for different response patterns.',
        severity: 'info',
        suggestion: domain.suggestion
      });
    }
  }
  
  return {
    domain,
    issues,
    isValid: issues.filter(i => i.severity === 'warning' || i.severity === 'error').length === 0
  };
};

// Key used for issues that belong to the whole survey rather than one node
// (e.g. "there is no Start node"). The validation panel renders these under
// the survey name instead of trying to jump to a node that doesn't exist.
export const SURVEY_LEVEL_KEY = '__survey__';

/** Every node id reachable from startId by walking edges forwards. */
const reachableFrom = (startId, edges) => {
  const outgoing = new Map();
  edges.forEach(e => {
    if (!outgoing.has(e.source)) outgoing.set(e.source, []);
    outgoing.get(e.source).push(e.target);
  });
  const seen = new Set();
  const queue = [startId];
  while (queue.length) {
    const cur = queue.shift();
    if (seen.has(cur)) continue;
    seen.add(cur);
    (outgoing.get(cur) || []).forEach(t => { if (!seen.has(t)) queue.push(t); });
  }
  return seen;
};

export const validateSurveyFlow = (nodes, edges) => {
  const validationResults = {};

  const questionNodes = nodes.filter(node => node.type === 'question');

  questionNodes.forEach(node => {
    const outgoingEdges = edges.filter(edge => edge.source === node.id);
    validationResults[node.id] = validateResponseCoverage(node, outgoingEdges);
  });

  // Add an issue to a node's result, creating the entry if this is the first
  // problem found for it. isValid is recomputed as "no errors and no warnings"
  // to match validateResponseCoverage.
  const addIssue = (nodeId, domainType, issue) => {
    const entry = validationResults[nodeId] || { domain: { type: domainType }, issues: [], isValid: true };
    entry.issues = [...entry.issues, issue];
    entry.isValid = entry.issues.filter(i => i.severity === 'error' || i.severity === 'warning').length === 0;
    validationResults[nodeId] = entry;
  };

  // WP-2 item 6: a package node is a flow-through node — like a question it must
  // have at least one inbound and one outbound edge, or the compiled flow has a
  // hole where the package expands. Surface unwired packages as errors.
  const hasInbound = new Set(edges.map(e => e.target));
  const hasOutbound = new Set(edges.map(e => e.source));
  nodes.filter(node => node.type === 'package').forEach(node => {
    const issues = [];
    if (!hasInbound.has(node.id)) {
      issues.push({
        type: 'no_inbound',
        severity: 'error',
        message: 'Package has no inbound edge — nothing routes into it.',
        suggestion: 'Connect an edge from where this package should begin.'
      });
    }
    if (!hasOutbound.has(node.id)) {
      issues.push({
        type: 'no_outbound',
        severity: 'error',
        message: 'Package has no outbound edge — the flow cannot resume after it.',
        suggestion: 'Connect an edge to wherever the flow should continue.'
      });
    }
    validationResults[node.id] = { domain: { type: 'package' }, issues, isValid: issues.length === 0 };
  });

  // Labels-as-expressions (ruling 2026-08-02): a decision branch's label IS its
  // routing expression. A branch with no label (or a default "Branch N" one)
  // gives the kernel nothing to route on — exactly the failure that misrouted
  // the Rand Health survey. Legacy edges that still carry a condition string
  // are exempt: export synthesizes their label from the condition.
  nodes.filter(node => node.type === 'decision').forEach(node => {
    const branchEdges = edges.filter(edge => edge.source === node.id);
    const issues = [];
    branchEdges.forEach((edge, i) => {
      const label = String(edge.data?.label || '').trim();
      const legacyCondition = String(edge.data?.condition || '').trim();
      if (!label && !legacyCondition) {
        issues.push({
          type: 'unlabeled_branch',
          severity: 'error',
          message: `Branch ${i + 1} (to ${edge.target}) has no label — the survey cannot route it.`,
          suggestion: 'Enter the answer value or expression for this branch (e.g. "Not at all", "!= Not at all", "Other").'
        });
      } else if (/^branch\s*\d+$/i.test(label)) {
        issues.push({
          type: 'default_branch_label',
          severity: 'error',
          message: `Branch label "${label}" is a placeholder, not a routing expression.`,
          suggestion: 'Replace it with the answer value or expression that should take this branch.'
        });
      }
    });
    if (branchEdges.length < 2) {
      issues.push({
        type: 'degenerate_decision',
        severity: 'warning',
        message: `Decision has ${branchEdges.length} outgoing branch(es); it needs at least two to decide anything.`,
        suggestion: 'Add branches, or remove the decision node.'
      });
    }
    if (issues.length > 0 || !validationResults[node.id]) {
      validationResults[node.id] = { domain: { type: 'decision' }, issues, isValid: issues.filter(i => i.severity === 'error').length === 0 };
    }
  });

  // Structural checks (2026-08-04). The kernel walks edges and nothing else: a
  // node with no path from Start is never asked, and a survey with no Start
  // node makes the kernel throw NO_START before the first question is spoken.
  // Neither failure was caught here before, so four loose nodes validated
  // clean. These are advisory like everything else — Validate never blocks a
  // save; the deploy endpoint is what actually refuses to publish a broken flow.
  const startNodes = nodes.filter(node => node.type === 'start');
  const endNodes = nodes.filter(node => node.type === 'end');
  const surveyIssues = [];

  if (startNodes.length === 0) {
    surveyIssues.push({
      type: 'no_start_node',
      severity: 'error',
      message: 'This survey has no Start node, so it cannot begin.',
      suggestion: 'Add a Start node and connect it to your first question.'
    });
  } else if (startNodes.length > 1) {
    surveyIssues.push({
      type: 'multiple_start_nodes',
      severity: 'error',
      message: `This survey has ${startNodes.length} Start nodes, so it does not know where to begin.`,
      suggestion: 'Delete the extra Start nodes so only one remains.'
    });
  }

  if (endNodes.length === 0) {
    surveyIssues.push({
      type: 'no_end_node',
      severity: 'warning',
      message: 'This survey has no End node, so it just stops when the flow runs out.',
      suggestion: 'Add an End node and connect your last question to it.'
    });
  }

  // Unreachable nodes: the survey runs, but these are silently never asked.
  if (startNodes.length === 1) {
    const reachable = reachableFrom(startNodes[0].id, edges);
    const stranded = nodes.filter(node => !reachable.has(node.id));
    stranded.forEach(node => {
      // A package node already reports no_inbound, which says the same thing in
      // its own words. Don't tell the author twice.
      const alreadySaid = (validationResults[node.id]?.issues || [])
        .some(i => i.type === 'no_inbound');
      if (alreadySaid) return;
      addIssue(node.id, node.type || 'node', {
        type: 'unreachable',
        severity: 'error',
        message: 'Nothing connects to this node, so the survey will never reach it.',
        suggestion: 'Draw an edge into this node from somewhere on the Start node\'s path.'
      });
    });
    if (stranded.length > 0) {
      surveyIssues.push({
        type: 'unreachable_nodes',
        severity: 'error',
        message: `This survey will not run as drawn: ${stranded.length} node(s) are not connected to the flow.`,
        suggestion: 'Connect every node to the path that leads from Start.'
      });
    }
  }

  if (surveyIssues.length > 0) {
    validationResults[SURVEY_LEVEL_KEY] = {
      domain: { type: 'survey' },
      issues: surveyIssues,
      isValid: false
    };
  }

  return validationResults;
};